// GENERATED FILE — do not edit by hand.
// Source: docs/DETECTIONS.md §7.0 (master table).
// Regenerate with: npm run gen:meta   ·   verify with: npm run gen:meta -- --check

import type { DetectionKey, DetectionMeta } from './detections.js'

/**
 * Per-module metadata and factory defaults (SPEC.md §4.1).
 *
 * `deterministic` gates global-ban proposals; `serverVerifiable` gates the
 * ban-to-kick downgrade when the server could not confirm a client claim.
 */
export const DETECTION_META: Record<DetectionKey, DetectionMeta> = {
  godmode: {
    side: 'client', category: 'player', severity: 'critical',
    deterministic: false, serverVerifiable: true,
    defaults: { enabled: true, action: 'ban', strikes: 4, windowMs: 15000, screenshot: true },
  },
  health_over_max: {
    side: 'client', category: 'player', severity: 'high',
    deterministic: false, serverVerifiable: true,
    defaults: { enabled: true, action: 'kick', strikes: 2, windowMs: 15000, screenshot: true },
  },
  health_probe: {
    side: 'client', category: 'player', severity: 'high',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'kick', strikes: 5, windowMs: 60000, screenshot: true },
  },
  armor_over_max: {
    side: 'client', category: 'player', severity: 'medium',
    deterministic: false, serverVerifiable: true,
    defaults: { enabled: true, action: 'kick', strikes: 2, windowMs: 15000, screenshot: false },
  },
  invisible: {
    side: 'client', category: 'player', severity: 'high',
    deterministic: false, serverVerifiable: true,
    defaults: { enabled: true, action: 'kick', strikes: 4, windowMs: 15000, screenshot: true },
  },
  noclip: {
    side: 'client', category: 'player', severity: 'high',
    deterministic: false, serverVerifiable: true,
    defaults: { enabled: true, action: 'kick', strikes: 3, windowMs: 10000, screenshot: true },
  },
  speed_hack: {
    side: 'client', category: 'player', severity: 'high',
    deterministic: false, serverVerifiable: true,
    defaults: { enabled: true, action: 'kick', strikes: 4, windowMs: 10000, screenshot: false },
  },
  teleport: {
    side: 'client', category: 'player', severity: 'medium',
    deterministic: false, serverVerifiable: true,
    defaults: { enabled: true, action: 'kick', strikes: 2, windowMs: 15000, screenshot: false },
  },
  spectate: {
    side: 'client', category: 'player', severity: 'high',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'kick', strikes: 3, windowMs: 15000, screenshot: true },
  },
  freecam: {
    side: 'client', category: 'player', severity: 'high',
    deterministic: false, serverVerifiable: true,
    defaults: { enabled: true, action: 'kick', strikes: 4, windowMs: 15000, screenshot: true },
  },
  super_jump: {
    side: 'client', category: 'player', severity: 'medium',
    deterministic: false, serverVerifiable: true,
    defaults: { enabled: true, action: 'kick', strikes: 3, windowMs: 10000, screenshot: false },
  },
  infinite_stamina: {
    side: 'client', category: 'player', severity: 'medium',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'kick', strikes: 8, windowMs: 20000, screenshot: false },
  },
  night_vision: {
    side: 'client', category: 'player', severity: 'low',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'log', strikes: 3, windowMs: 15000, screenshot: false },
  },
  blacklisted_ped: {
    side: 'client', category: 'player', severity: 'high',
    deterministic: false, serverVerifiable: true,
    defaults: { enabled: true, action: 'kick', strikes: 1, windowMs: 10000, screenshot: true },
  },
  blacklisted_weapon: {
    side: 'client', category: 'weapon', severity: 'high',
    deterministic: false, serverVerifiable: true,
    defaults: { enabled: true, action: 'kick', strikes: 2, windowMs: 15000, screenshot: true },
  },
  weapon_gained: {
    side: 'client', category: 'weapon', severity: 'medium',
    deterministic: false, serverVerifiable: true,
    defaults: { enabled: true, action: 'log', strikes: 3, windowMs: 30000, screenshot: false },
  },
  infinite_ammo: {
    side: 'client', category: 'weapon', severity: 'medium',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'kick', strikes: 12, windowMs: 20000, screenshot: false },
  },
  explosive_ammo: {
    side: 'client', category: 'weapon', severity: 'high',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'kick', strikes: 2, windowMs: 10000, screenshot: true },
  },
  damage_modifier: {
    side: 'client', category: 'weapon', severity: 'critical',
    deterministic: true, serverVerifiable: true,
    defaults: { enabled: true, action: 'ban', strikes: 3, windowMs: 15000, screenshot: true },
  },
  weapon_damage_meta: {
    side: 'client', category: 'weapon', severity: 'high',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'kick', strikes: 3, windowMs: 15000, screenshot: true },
  },
  silent_aim: {
    side: 'client', category: 'combat', severity: 'high',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'kick', strikes: 4, windowMs: 20000, screenshot: true },
  },
  shot_cadence: {
    side: 'client', category: 'combat', severity: 'high',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'kick', strikes: 3, windowMs: 15000, screenshot: true },
  },
  no_recoil: {
    side: 'client', category: 'combat', severity: 'high',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'kick', strikes: 4, windowMs: 20000, screenshot: true },
  },
  vehicle_modifier: {
    side: 'client', category: 'vehicle', severity: 'high',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'kick', strikes: 3, windowMs: 15000, screenshot: false },
  },
  vehicle_speed: {
    side: 'client', category: 'vehicle', severity: 'medium',
    deterministic: false, serverVerifiable: true,
    defaults: { enabled: true, action: 'kick', strikes: 4, windowMs: 15000, screenshot: false },
  },
  vehicle_handling: {
    side: 'client', category: 'vehicle', severity: 'medium',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'kick', strikes: 3, windowMs: 20000, screenshot: false },
  },
  vehicle_godmode: {
    side: 'client', category: 'vehicle', severity: 'medium',
    deterministic: false, serverVerifiable: true,
    defaults: { enabled: true, action: 'kick', strikes: 5, windowMs: 30000, screenshot: false },
  },
  vehicle_invisible: {
    side: 'client', category: 'vehicle', severity: 'high',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'kick', strikes: 4, windowMs: 15000, screenshot: true },
  },
  vehicle_noclip: {
    side: 'client', category: 'vehicle', severity: 'high',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'kick', strikes: 3, windowMs: 10000, screenshot: true },
  },
  vehicle_gravity: {
    side: 'client', category: 'vehicle', severity: 'medium',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'kick', strikes: 3, windowMs: 15000, screenshot: false },
  },
  player_blips: {
    side: 'client', category: 'world', severity: 'medium',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'log', strikes: 2, windowMs: 15000, screenshot: true },
  },
  entity_spam_client: {
    side: 'client', category: 'world', severity: 'medium',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'log', strikes: 2, windowMs: 10000, screenshot: false },
  },
  menu_texture: {
    side: 'client', category: 'integrity', severity: 'critical',
    deterministic: true, serverVerifiable: false,
    defaults: { enabled: true, action: 'ban', strikes: 1, windowMs: 10000, screenshot: true },
  },
  menu_key: {
    side: 'client', category: 'integrity', severity: 'low',
    deterministic: false, serverVerifiable: false, lockedAction: 'log',
    defaults: { enabled: true, action: 'log', strikes: 2, windowMs: 10000, screenshot: false },
  },
  resource_injection: {
    side: 'client', category: 'integrity', severity: 'critical',
    deterministic: true, serverVerifiable: true,
    defaults: { enabled: true, action: 'ban', strikes: 1, windowMs: 30000, screenshot: true },
  },
  resource_stopped: {
    side: 'client', category: 'integrity', severity: 'critical',
    deterministic: true, serverVerifiable: true,
    defaults: { enabled: true, action: 'ban', strikes: 2, windowMs: 60000, screenshot: false },
  },
  client_integrity: {
    side: 'client', category: 'integrity', severity: 'medium',
    deterministic: false, serverVerifiable: false, lockedAction: 'log',
    defaults: { enabled: true, action: 'log', strikes: 1, windowMs: 60000, screenshot: true },
  },
  client_honeypot: {
    side: 'client', category: 'integrity', severity: 'critical',
    deterministic: true, serverVerifiable: false,
    defaults: { enabled: true, action: 'ban', strikes: 1, windowMs: 60000, screenshot: true },
  },
  heartbeat_missing: {
    side: 'server', category: 'network', severity: 'high',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'kick', strikes: 1, windowMs: 60000, screenshot: false },
  },
  honeypot_event: {
    side: 'server', category: 'integrity', severity: 'critical',
    deterministic: true, serverVerifiable: false,
    defaults: { enabled: true, action: 'ban', strikes: 1, windowMs: 60000, screenshot: true },
  },
  honeypot_export: {
    side: 'server', category: 'integrity', severity: 'critical',
    deterministic: true, serverVerifiable: false,
    defaults: { enabled: true, action: 'ban', strikes: 1, windowMs: 60000, screenshot: false },
  },
  honeypot_command: {
    side: 'server', category: 'integrity', severity: 'critical',
    deterministic: true, serverVerifiable: false,
    defaults: { enabled: true, action: 'ban', strikes: 1, windowMs: 60000, screenshot: true },
  },
  explosion_spam: {
    side: 'server', category: 'world', severity: 'high',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'kick', strikes: 5, windowMs: 10000, screenshot: true },
  },
  explosion_blacklist: {
    side: 'server', category: 'world', severity: 'critical',
    deterministic: true, serverVerifiable: false,
    defaults: { enabled: true, action: 'ban', strikes: 1, windowMs: 10000, screenshot: true },
  },
  weapon_damage_event: {
    side: 'server', category: 'weapon', severity: 'critical',
    deterministic: true, serverVerifiable: false,
    defaults: { enabled: true, action: 'ban', strikes: 2, windowMs: 10000, screenshot: true },
  },
  give_weapon_event: {
    side: 'server', category: 'weapon', severity: 'medium',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'log', strikes: 3, windowMs: 30000, screenshot: false },
  },
  remove_weapon_event: {
    side: 'server', category: 'weapon', severity: 'low',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'log', strikes: 3, windowMs: 30000, screenshot: false },
  },
  server_headshot_ratio: {
    side: 'server', category: 'combat', severity: 'high',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'log', strikes: 1, windowMs: 60000, screenshot: true },
  },
  magic_bullet: {
    side: 'server', category: 'combat', severity: 'critical',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'kick', strikes: 2, windowMs: 15000, screenshot: true },
  },
  fire_spam: {
    side: 'server', category: 'world', severity: 'medium',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'kick', strikes: 4, windowMs: 10000, screenshot: false },
  },
  clear_ped_tasks: {
    side: 'server', category: 'world', severity: 'medium',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'kick', strikes: 3, windowMs: 10000, screenshot: false },
  },
  projectile_spam: {
    side: 'server', category: 'world', severity: 'medium',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'kick', strikes: 8, windowMs: 10000, screenshot: false },
  },
  ptfx_blacklist: {
    side: 'server', category: 'world', severity: 'low',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'log', strikes: 3, windowMs: 15000, screenshot: false },
  },
  entity_spawn_rate: {
    side: 'server', category: 'world', severity: 'high',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'kick', strikes: 3, windowMs: 60000, screenshot: false },
  },
  entity_blacklisted_model: {
    side: 'server', category: 'world', severity: 'critical',
    deterministic: true, serverVerifiable: false,
    defaults: { enabled: true, action: 'ban', strikes: 1, windowMs: 10000, screenshot: true },
  },
  entity_unauthorized_script: {
    side: 'server', category: 'world', severity: 'medium',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'log', strikes: 3, windowMs: 30000, screenshot: false },
  },
  server_godmode: {
    side: 'server', category: 'player', severity: 'critical',
    deterministic: true, serverVerifiable: true,
    defaults: { enabled: true, action: 'ban', strikes: 3, windowMs: 15000, screenshot: true },
  },
  server_super_jump: {
    side: 'server', category: 'player', severity: 'high',
    deterministic: false, serverVerifiable: true,
    defaults: { enabled: true, action: 'kick', strikes: 3, windowMs: 15000, screenshot: false },
  },
  server_freecam: {
    side: 'server', category: 'player', severity: 'high',
    deterministic: false, serverVerifiable: true,
    defaults: { enabled: true, action: 'kick', strikes: 2, windowMs: 30000, screenshot: true },
  },
  server_teleport: {
    side: 'server', category: 'player', severity: 'medium',
    deterministic: false, serverVerifiable: true,
    defaults: { enabled: true, action: 'kick', strikes: 2, windowMs: 15000, screenshot: false },
  },
  server_speed: {
    side: 'server', category: 'player', severity: 'medium',
    deterministic: false, serverVerifiable: true,
    defaults: { enabled: true, action: 'kick', strikes: 4, windowMs: 15000, screenshot: false },
  },
  chat_spam: {
    side: 'server', category: 'network', severity: 'low',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'kick', strikes: 3, windowMs: 10000, screenshot: false },
  },
  chat_blacklisted_string: {
    side: 'server', category: 'network', severity: 'medium',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'log', strikes: 1, windowMs: 60000, screenshot: false },
  },
  event_spam: {
    side: 'server', category: 'network', severity: 'medium',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'kick', strikes: 3, windowMs: 10000, screenshot: false },
  },
  blacklisted_event: {
    side: 'server', category: 'network', severity: 'critical',
    deterministic: true, serverVerifiable: false,
    defaults: { enabled: true, action: 'ban', strikes: 1, windowMs: 60000, screenshot: true },
  },
  safe_event_invalid: {
    side: 'server', category: 'network', severity: 'high',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'kick', strikes: 3, windowMs: 30000, screenshot: false },
  },
  event_rule: {
    side: 'server', category: 'network', severity: 'high',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'log', strikes: 1, windowMs: 60000, screenshot: false },
  },
  resource_list_mismatch: {
    side: 'server', category: 'integrity', severity: 'high',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'kick', strikes: 2, windowMs: 60000, screenshot: false },
  },
  connect_offline: {
    side: 'server', category: 'connection', severity: 'low',
    deterministic: false, serverVerifiable: false, lockedAction: 'log',
    defaults: { enabled: true, action: 'log', strikes: 1, windowMs: 60000, screenshot: false },
  },
  custom: {
    side: 'server', category: 'integrity', severity: 'medium',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'log', strikes: 1, windowMs: 60000, screenshot: false },
  },
  connect_ban_evasion: {
    side: 'cloud', category: 'connection', severity: 'high',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'kick', strikes: 1, windowMs: 60000, screenshot: false },
  },
  connect_missing_identifier: {
    side: 'cloud', category: 'connection', severity: 'medium',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'kick', strikes: 1, windowMs: 60000, screenshot: false },
  },
  connect_vpn: {
    side: 'cloud', category: 'connection', severity: 'low',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: false, action: 'kick', strikes: 1, windowMs: 60000, screenshot: false },
  },
  connect_name_filter: {
    side: 'cloud', category: 'connection', severity: 'low',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'kick', strikes: 1, windowMs: 60000, screenshot: false },
  },
  connect_low_trust: {
    side: 'cloud', category: 'connection', severity: 'medium',
    deterministic: false, serverVerifiable: false,
    defaults: { enabled: true, action: 'log', strikes: 1, windowMs: 60000, screenshot: false },
  },
}
